import { useChooseContractLanguageMutation, useRecordLanguagePreferenceMutation } from '@/services/contractSigning';
import { PalmettoButton, PalmettoOptionGroup, PalmettoTextInput } from '@palmetto/dsm-components';
import { ChangeEvent, useCallback, useMemo, useState } from 'react';

import { CONGA_LANGUAGE_OTHER, CongaLanguageOption, CongaPresigningState, Locale } from 'types';

import { Copy, copyText, displayLocalesFor, inlineCopy, SupportContact } from './ContractSigningCopy';
import { isConflict } from './contractSigningErrors';

interface LanguageStepProps {
  state: CongaPresigningState;
  inviteToken: string;
  onConflict: () => void;
}

const BOTH = [Locale.en, Locale.es];

const LANGUAGE_SET_HEADINGS = {
  [Locale.en]: 'languageSetHeadingEnglish',
  [Locale.es]: 'languageSetHeadingSpanish',
} as const;

const useLanguageOptions = (withSpanishDetail: boolean) =>
  useMemo(
    () => [
      { value: Locale.en, label: copyText(Locale.en, 'languageEnglish') },
      {
        value: Locale.es,
        label: copyText(Locale.es, 'languageSpanish'),
        ...(withSpanishDetail && { subline: inlineCopy(BOTH, 'languageSpanishDetail') }),
      },
      { value: CONGA_LANGUAGE_OTHER, label: inlineCopy(BOTH, 'languageOther') },
    ],
    [withSpanishDetail],
  );

interface LanguageChoiceFormProps {
  name: string;
  label: string;
  locales: Locale[];
  withSpanishDetail: boolean;
  isSubmitting: boolean;
  onSubmit: (language: CongaLanguageOption, otherLanguage?: string) => void;
}

/** Nothing is preselected, and the account's profile language is deliberately never used as a default. */
const LanguageChoiceForm = ({
  name,
  label,
  locales,
  withSpanishDetail,
  isSubmitting,
  onSubmit,
}: LanguageChoiceFormProps) => {
  const options = useLanguageOptions(withSpanishDetail);
  const [language, setLanguage] = useState<CongaLanguageOption | ''>('');
  const [otherLanguage, setOtherLanguage] = useState('');
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);

  const handleLanguageChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => setLanguage(event.target.value as CongaLanguageOption),
    [],
  );
  const handleOtherChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => setOtherLanguage(event.target.value),
    [],
  );

  const isOther = language === CONGA_LANGUAGE_OTHER;
  const missingOther = isOther && !otherLanguage.trim();

  const languageError = useMemo(
    () =>
      hasAttemptedSubmit && !language
        ? { type: 'error' as const, text: inlineCopy(locales, 'languageRequired') }
        : undefined,
    [hasAttemptedSubmit, language, locales],
  );
  const otherLanguageError = useMemo(
    () =>
      hasAttemptedSubmit && missingOther
        ? { type: 'error' as const, text: inlineCopy(locales, 'languageOtherRequired') }
        : undefined,
    [hasAttemptedSubmit, missingOther, locales],
  );

  const handleSubmit = useCallback(() => {
    setHasAttemptedSubmit(true);
    if (!language || missingOther) return;
    onSubmit(language, isOther ? otherLanguage.trim() : undefined);
  }, [language, missingOther, isOther, otherLanguage, onSubmit]);

  return (
    <div className="flex flex-col gap-6">
      <PalmettoOptionGroup
        type="radio"
        name={name}
        label={label}
        value={language}
        onChange={handleLanguageChange}
        options={options}
        validationMessage={languageError}
      />
      {isOther && (
        <PalmettoTextInput
          id={`${name}-other`}
          label={inlineCopy(locales, 'languageOtherLabel')}
          value={otherLanguage}
          onChange={handleOtherChange}
          maxLength={100}
          validationMessage={otherLanguageError}
        />
      )}
      <PalmettoButton type="button" emphasis="primary" size="large" loading={isSubmitting} onClick={handleSubmit}>
        {inlineCopy(locales, 'continue')}
      </PalmettoButton>
    </div>
  );
};

/** Screens 1 and 5a: the first customer sets the contract language, or learns theirs isn't offered. */
const FirstCustomerLanguage = ({ inviteToken, onConflict }: Omit<LanguageStepProps, 'state'>) => {
  const [chooseLanguage, { isLoading, isError, error }] = useChooseContractLanguageMutation();
  const [notOfferedLanguage, setNotOfferedLanguage] = useState<string | null>(null);

  const handleSubmit = useCallback(
    async (language: CongaLanguageOption, otherLanguage?: string) => {
      try {
        const { outcome } = await chooseLanguage({ inviteToken, language, otherLanguage }).unwrap();
        if (outcome === 'notOffered') setNotOfferedLanguage(otherLanguage ?? '');
      } catch (caught) {
        if (isConflict(caught)) onConflict();
      }
    },
    [chooseLanguage, inviteToken, onConflict],
  );
  const handleBack = useCallback(() => setNotOfferedLanguage(null), []);
  const notOfferedVars = useMemo(() => ({ language: notOfferedLanguage ?? '' }), [notOfferedLanguage]);

  if (notOfferedLanguage !== null) {
    return (
      <div className="flex flex-col gap-6">
        <Copy copyKey="notOfferedHeading" locales={BOTH} vars={notOfferedVars} variant="heading" />
        <Copy copyKey="notOfferedBody" locales={BOTH} />
        <SupportContact locales={BOTH} />
        <PalmettoButton type="button" emphasis="secondary" size="large" onClick={handleBack}>
          {inlineCopy(BOTH, 'notOfferedChooseAnother')}
        </PalmettoButton>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <Copy copyKey="languageHeading" locales={BOTH} variant="heading" />
      <Copy copyKey="languageHelp" locales={BOTH} />
      <LanguageChoiceForm
        name="contract-language"
        label={inlineCopy(BOTH, 'languageHeading')}
        locales={BOTH}
        withSpanishDetail
        isSubmitting={isLoading}
        onSubmit={handleSubmit}
      />
      {isError && !isConflict(error) && <Copy copyKey="genericError" locales={BOTH} variant="error" />}
    </div>
  );
};

/** Screen 1b: the language is already set; this customer's own preference is recorded but changes nothing. */
const LaterCustomerLanguage = ({
  contractLanguage,
  inviteToken,
}: {
  contractLanguage: Locale;
  inviteToken: string;
}) => {
  const locales = displayLocalesFor(contractLanguage);
  const [recordPreference, { isLoading, isError }] = useRecordLanguagePreferenceMutation();

  const handleSubmit = useCallback(
    async (language: CongaLanguageOption, otherLanguage?: string) => {
      try {
        await recordPreference({ inviteToken, language, otherLanguage }).unwrap();
      } catch {
        // isError renders the message below; the signer can retry.
      }
    },
    [recordPreference, inviteToken],
  );

  return (
    <div className="flex flex-col gap-6">
      <Copy copyKey={LANGUAGE_SET_HEADINGS[contractLanguage]} locales={locales} variant="heading" />
      <Copy copyKey="languageSetBody" locales={locales} />
      <SupportContact locales={locales} />
      <LanguageChoiceForm
        name="language-preference"
        label={inlineCopy(locales, 'languagePreferenceLabel')}
        locales={locales}
        withSpanishDetail={false}
        isSubmitting={isLoading}
        onSubmit={handleSubmit}
      />
      {isError && <Copy copyKey="genericError" locales={locales} variant="error" />}
    </div>
  );
};

export const LanguageStep = ({ state, inviteToken, onConflict }: LanguageStepProps) =>
  state.contractLanguage ? (
    <LaterCustomerLanguage contractLanguage={state.contractLanguage} inviteToken={inviteToken} />
  ) : (
    <FirstCustomerLanguage inviteToken={inviteToken} onConflict={onConflict} />
  );
